import { useEffect, useState, useMemo } from "react";
import { Plus, Save, Trash2, Edit3, X, Copy, Lock } from "lucide-react";
import { api, formatErr } from "@/lib/api";

export default function PackagesPage() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [talents, setTalents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [showInternal, setShowInternal] = useState(true);

  const load = () => api.get("/admin/com/packages").then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/admin/com/products").then(setProducts).catch(() => setProducts([]));
    api.get("/admin/com/talents").then(setTalents).catch(() => setTalents([]));
  }, []);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const talentMap = useMemo(() => Object.fromEntries(talents.map((t) => [t.id, t])), [talents]);

  const itemUnitPrice = (it) => {
    if (it.price_override !== undefined && it.price_override !== null && it.price_override !== "") {
      return Number(it.price_override) || 0;
    }
    const p = productMap[it.product_id];
    return p ? Number(p.base_price || 0) : 0;
  };

  const totalAuto = useMemo(() => {
    if (!editing) return 0;
    return (editing.items || []).reduce((acc, it) => acc + itemUnitPrice(it) * (Number(it.qty) || 1), 0);
  }, [editing, productMap]); // eslint-disable-line

  const finalPrice = useMemo(() => {
    const disc = Number(editing?.discount_pct) || 0;
    return Math.round(totalAuto * (1 - disc / 100) * 100) / 100;
  }, [totalAuto, editing?.discount_pct]);

  const internalCostsTotal = useMemo(() => {
    if (!editing) return 0;
    return (editing.internal_costs || []).reduce((acc, c) => acc + (Number(c.amount) || 0), 0);
  }, [editing]);

  const netProfit = useMemo(() => Math.round((finalPrice - internalCostsTotal) * 100) / 100, [finalPrice, internalCostsTotal]);
  const marginPct = useMemo(() => finalPrice > 0 ? Math.round((netProfit / finalPrice) * 10000) / 100 : 0, [finalPrice, netProfit]);

  const save = async () => {
    setError("");
    try {
      const body = { ...editing, total_auto: totalAuto, final_price: finalPrice };
      if (editing.id && items.find((x) => x.id === editing.id)) {
        await api.put(`/admin/com/packages/${editing.id}`, body);
      } else {
        delete body.id;
        await api.post("/admin/com/packages", body);
      }
      setEditing(null); load();
    } catch (e) { setError(formatErr(e.response?.data?.detail) || "Error"); }
  };

  const addCost = (type) => {
    const newCost = { id: `c${Date.now()}`, type, description: "", amount: 0, currency: editing.currency || "BOB", talent_id: "", notes: "" };
    if (type === "talent" && talents.length) {
      newCost.talent_id = talents[0].id;
      newCost.amount = Number(talents[0].base_price || 0);
      newCost.description = `Talento: ${talents[0].artist_name}`;
    }
    setEditing({ ...editing, internal_costs: [...(editing.internal_costs || []), newCost] });
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar paquete?")) return;
    try { await api.delete(`/admin/com/packages/${id}`); load(); } catch (e) { setError(formatErr(e.response?.data?.detail)); }
  };

  const dup = async (id) => {
    try { await api.post(`/admin/com/packages/${id}/duplicate`, {}); load(); } catch (e) { setError(formatErr(e.response?.data?.detail)); }
  };

  const addItem = () => {
    if (!products.length) return;
    setEditing({ ...editing, items: [...(editing.items || []), { product_id: products[0].id, qty: 1, frequency: "" }] });
  };

  return (
    <div data-testid="commercial-packages">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">Paquetes Comerciales</h1>
          <p className="text-sm text-muted-foreground">Combos de productos comerciales · Total: {items.length}</p>
        </div>
        <button onClick={() => setEditing({ name: "", description: "", items: [], discount_pct: 0, currency: "BOB", status: "active" })} data-testid="package-new" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> Nuevo paquete
        </button>
      </div>

      {editing && (
        <div className="border border-primary rounded-sm p-6 mb-6 bg-secondary/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">{editing.id ? "Editar paquete" : "Nuevo paquete"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Nombre del paquete</label>
              <input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Duración</label>
              <input value={editing.duration || ""} placeholder="1 mes / Por campaña" onChange={(e) => setEditing({ ...editing, duration: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Descripción</label>
              <textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none resize-none" />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-bold">Productos incluidos ({(editing.items || []).length})</h4>
              <button onClick={addItem} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary"><Plus className="w-3 h-3" /> Agregar producto</button>
            </div>
            {(editing.items || []).map((it, i) => {
              const p = productMap[it.product_id];
              const unit = itemUnitPrice(it);
              const subtotal = unit * (Number(it.qty) || 1);
              const isOverride = it.price_override !== undefined && it.price_override !== null && it.price_override !== "";
              return (
                <div key={i} className="flex gap-2 items-end mb-2 border border-border rounded-sm p-3 bg-background">
                  <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Producto</label>
                    <select value={it.product_id || ""} onChange={(e) => { const a = [...editing.items]; a[i] = { ...it, product_id: e.target.value }; setEditing({ ...editing, items: a }); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm">
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.currency} {p.base_price}</option>)}
                    </select>
                  </div>
                  <div className="w-20">
                    <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Cant.</label>
                    <input type="number" min="1" value={it.qty || 1} onChange={(e) => { const a = [...editing.items]; a[i] = { ...it, qty: Number(e.target.value) }; setEditing({ ...editing, items: a }); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" />
                  </div>
                  <div className="w-32">
                    <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block flex items-center gap-1">
                      Precio unit.{isOverride && <span className="text-amber-500" title="Override activo">●</span>}
                    </label>
                    <input
                      type="number" step="0.01"
                      data-testid={`item-price-override-${i}`}
                      value={isOverride ? it.price_override : (p?.base_price ?? "")}
                      placeholder={p ? String(p.base_price) : ""}
                      onChange={(e) => { const a = [...editing.items]; a[i] = { ...it, price_override: e.target.value === "" ? null : Number(e.target.value) }; setEditing({ ...editing, items: a }); }}
                      className={`w-full h-9 px-2 bg-background border rounded-sm text-sm ${isOverride ? "border-amber-500" : "border-border"}`}
                    />
                    {isOverride && (
                      <button type="button" onClick={() => { const a = [...editing.items]; a[i] = { ...it, price_override: null }; setEditing({ ...editing, items: a }); }} className="text-[9px] text-muted-foreground underline mt-0.5">restablecer base</button>
                    )}
                  </div>
                  <div className="w-32">
                    <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Frecuencia</label>
                    <input value={it.frequency || ""} placeholder="1/sem" onChange={(e) => { const a = [...editing.items]; a[i] = { ...it, frequency: e.target.value }; setEditing({ ...editing, items: a }); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" />
                  </div>
                  <div className="w-28 text-right">
                    <p className="text-[10px] uppercase tracking-widest font-bold mb-1">Subtotal</p>
                    <p className="font-display font-black">{editing.currency || "BOB"} {subtotal.toLocaleString()}</p>
                  </div>
                  <button onClick={() => setEditing({ ...editing, items: editing.items.filter((_, x) => x !== i) })} className="p-2 text-destructive hover:bg-destructive/10 rounded-sm"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 grid md:grid-cols-4 gap-4 items-end border-t border-border pt-5">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Moneda</label>
              <select value={editing.currency || "BOB"} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
                <option value="BOB">BOB · Boliviano</option>
                <option value="USD">USD · Dólar</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Descuento %</label>
              <input type="number" step="0.5" value={editing.discount_pct || 0} onChange={(e) => setEditing({ ...editing, discount_pct: Number(e.target.value) })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Total automático</label>
              <p className="h-10 flex items-center font-display font-bold text-lg">{editing.currency || "BOB"} {totalAuto.toLocaleString()}</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block text-primary">Precio final</label>
              <p className="h-10 flex items-center font-display font-black text-2xl text-primary">{editing.currency || "BOB"} {finalPrice.toLocaleString()}</p>
            </div>
          </div>

          {/* Internal accounting (private) */}
          <div className="mt-8 border-2 border-dashed border-amber-500/40 bg-amber-500/5 rounded-sm p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h4 className="font-display font-bold flex items-center gap-2"><Lock className="w-4 h-4 text-amber-500" /> Costos internos · contabilidad privada</h4>
                <p className="text-[11px] text-muted-foreground">⚠️ Estos datos NUNCA se muestran al cliente, ni en propuestas públicas, ni a productores. Solo super admin y comercial.</p>
              </div>
              <button onClick={() => setShowInternal((s) => !s)} type="button" className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground">{showInternal ? "Ocultar" : "Mostrar"}</button>
            </div>
            {showInternal && (
              <>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button onClick={() => addCost("talent")} type="button" data-testid="add-cost-talent" className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary"><Plus className="w-3 h-3" /> Talento</button>
                  <button onClick={() => addCost("viatic")} type="button" className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary"><Plus className="w-3 h-3" /> Viáticos</button>
                  <button onClick={() => addCost("production")} type="button" className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary"><Plus className="w-3 h-3" /> Producción</button>
                  <button onClick={() => addCost("logistics")} type="button" className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary"><Plus className="w-3 h-3" /> Logística</button>
                  <button onClick={() => addCost("equipment")} type="button" className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary"><Plus className="w-3 h-3" /> Equipos</button>
                  <button onClick={() => addCost("other")} type="button" className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary"><Plus className="w-3 h-3" /> Otro</button>
                </div>
                {(editing.internal_costs || []).length === 0 && <p className="text-xs text-muted-foreground italic">Sin costos cargados.</p>}
                {(editing.internal_costs || []).map((c, i) => (
                  <div key={c.id || i} className="flex gap-2 items-end mb-2 border border-border rounded-sm p-2.5 bg-background">
                    <div className="w-32">
                      <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Tipo</label>
                      <select value={c.type || "other"} onChange={(e) => { const a = [...editing.internal_costs]; a[i] = { ...c, type: e.target.value }; setEditing({ ...editing, internal_costs: a }); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs">
                        <option value="talent">Talento</option>
                        <option value="viatic">Viáticos</option>
                        <option value="production">Producción</option>
                        <option value="logistics">Logística</option>
                        <option value="equipment">Equipos</option>
                        <option value="other">Otro</option>
                      </select>
                    </div>
                    {c.type === "talent" && (
                      <div className="w-44">
                        <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Talento</label>
                        <select value={c.talent_id || ""} onChange={(e) => {
                          const t = talentMap[e.target.value];
                          const a = [...editing.internal_costs];
                          a[i] = { ...c, talent_id: e.target.value, amount: t ? Number(t.base_price || c.amount || 0) : c.amount, description: t ? `Talento: ${t.artist_name}` : c.description };
                          setEditing({ ...editing, internal_costs: a });
                        }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs">
                          <option value="">— Seleccionar —</option>
                          {talents.map((t) => <option key={t.id} value={t.id}>{t.artist_name} · ref {t.currency} {t.base_price}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="flex-1">
                      <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Descripción</label>
                      <input value={c.description || ""} placeholder="Pasaje LP, hospedaje, equipo..." onChange={(e) => { const a = [...editing.internal_costs]; a[i] = { ...c, description: e.target.value }; setEditing({ ...editing, internal_costs: a }); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
                    </div>
                    <div className="w-28">
                      <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Monto</label>
                      <input type="number" step="0.01" value={c.amount || 0} onChange={(e) => { const a = [...editing.internal_costs]; a[i] = { ...c, amount: Number(e.target.value) }; setEditing({ ...editing, internal_costs: a }); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
                    </div>
                    <div className="w-20">
                      <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Moneda</label>
                      <select value={c.currency || "BOB"} onChange={(e) => { const a = [...editing.internal_costs]; a[i] = { ...c, currency: e.target.value }; setEditing({ ...editing, internal_costs: a }); }} className="w-full h-9 px-1 bg-background border border-border rounded-sm text-xs">
                        <option value="BOB">BOB</option><option value="USD">USD</option>
                      </select>
                    </div>
                    <button onClick={() => setEditing({ ...editing, internal_costs: editing.internal_costs.filter((_, x) => x !== i) })} type="button" className="p-1.5 text-destructive hover:bg-destructive/10 rounded-sm"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-amber-500/30">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Total costos</p>
                    <p className="font-display font-black text-xl">{editing.currency || "BOB"} {internalCostsTotal.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Ganancia neta</p>
                    <p className={`font-display font-black text-2xl ${netProfit >= 0 ? "text-emerald-500" : "text-destructive"}`} data-testid="net-profit">{editing.currency || "BOB"} {netProfit.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Margen</p>
                    <p className={`font-display font-black text-2xl ${marginPct >= 0 ? "text-emerald-500" : "text-destructive"}`}>{marginPct.toFixed(2)}%</p>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Notas internas (privadas)</label>
                  <textarea value={editing.internal_notes || ""} onChange={(e) => setEditing({ ...editing, internal_notes: e.target.value })} rows={2} placeholder="Detalles privados solo para tu equipo..." className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none resize-none" />
                </div>
              </>
            )}
          </div>

          {error && <p className="text-sm text-destructive mt-3">{error}</p>}
          <div className="mt-5 flex gap-2">
            <button onClick={save} className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold"><Save className="w-4 h-4" /> Guardar</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-border rounded-sm text-sm">Cancelar</button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Paquete</th>
              <th className="text-left p-3">Productos</th>
              <th className="text-left p-3">Duración</th>
              <th className="text-left p-3">Total</th>
              <th className="text-left p-3">Descuento</th>
              <th className="text-left p-3">Precio final</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-border hover:bg-secondary/30">
                <td className="p-3 font-semibold">{p.name}</td>
                <td className="p-3 text-xs text-muted-foreground">{(p.items || []).length} productos</td>
                <td className="p-3">{p.duration || "—"}</td>
                <td className="p-3">{p.currency} {(Number(p.total_auto) || 0).toLocaleString()}</td>
                <td className="p-3">{p.discount_pct || 0}%</td>
                <td className="p-3 font-bold text-primary">{p.currency} {(Number(p.final_price) || 0).toLocaleString()}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing({ ...p })} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-2 hover:border-primary"><Edit3 className="w-3 h-3" /> Editar</button>
                  <button onClick={() => dup(p.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-2 hover:border-primary"><Copy className="w-3 h-3" /> Duplicar</button>
                  <button onClick={() => del(p.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-destructive/40 text-destructive rounded-sm text-xs font-semibold hover:bg-destructive/10"><Trash2 className="w-3 h-3" /> Eliminar</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Sin paquetes todavía</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
